import { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, RefreshCw } from "lucide-react";

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: string[];
  requiredAccess?: string;
  fallback?: string;
}

export function RoleGuard({ children, allowedRoles, requiredAccess, fallback = "/dashboard" }: RoleGuardProps) {
  const { currentRole, loading, tenants, dashboardAccess } = useTenant();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center">
        <p className="mb-2 text-muted-foreground">You are not assigned to any organisation.</p>
        <p className="mb-6 text-sm text-muted-foreground">Contact your administrator to be added.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button variant="outline" className="gap-2" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button
            className="gap-2"
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="h-4 w-4" />
            Sign out to login
          </Button>
        </div>
      </div>
    );
  }

  if (!currentRole || !allowedRoles.includes(currentRole)) {
    return <Navigate to={fallback} replace />;
  }

  if (requiredAccess && !dashboardAccess.includes(requiredAccess)) {
    if (dashboardAccess.includes("dma_zero_pressure_test")) {
      return <Navigate to="/zero-pressure-test" replace />;
    }
    if (dashboardAccess.includes("valve_assessment")) {
      return <Navigate to="/boundary-valves" replace />;
    }
    if (dashboardAccess.includes("survey")) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <div className="flex min-h-screen flex-col items-center justify-center text-center p-4">
        <p className="text-muted-foreground mb-2">You do not have access to any dashboard.</p>
        <p className="text-sm text-muted-foreground">Contact your administrator to update your access.</p>
      </div>
    );
  }

  return <>{children}</>;
}
